import { resolvePriceComponentsTotal } from '@local/lib';
import { Button, MenuDropdown } from '@salesforce/design-system-react';
import { useState } from 'react';

import { IconCategory } from '../../../shared/types/Enum';
import { pluralize } from '../pluralize';
import { CONTRACT_PRICE_COMPONENTS, FIELD_HELP } from '../qualityFormulaMetadata';
import { AnimatedItem } from './AnimatedItem';
import { SpecSectionPanel } from './SpecSectionPanel';

interface PriceComponentAddButtonProps {
  onAdd: (key: string) => void;
  remainingKeys: string[];
}

// Preserves PRICE_COMPONENTS's own declaration order (base price first, then surcharges, then
// adjustments) rather than resorting groups alphabetically.
const groupsInCatalogOrder = (keys: string[]) => [
  ...new Set(
    CONTRACT_PRICE_COMPONENTS.filter(component => keys.includes(component.key)).map(component => component.group),
  ),
];

// The one concrete example named in the empty-state placeholder below — a real catalog entry (not
// an invented figure), so its label/value can never drift out of sync with what "Add Component"
// actually offers. Always "Premiums" specifically (not whichever catalog entry happens to be
// unselected) — a fixed, simple placeholder example per this feature's own current scope, not a
// "smartest available component" pick.
const EXAMPLE_PLACEHOLDER_COMPONENT = CONTRACT_PRICE_COMPONENTS.find(component => component.key === 'PREMIUMS');

/**
 * "+ Add Component" menu — lists only components not already in the table (an already-selected
 * one is removed via its own row's remove button instead), grouped the same way `PRICE_COMPONENTS`
 * groups them via SLDS `MenuDropdown`'s own `type: 'header'`/`divider` option entries. Hidden
 * entirely once every catalog component has been added.
 */
const PriceComponentAddButton: React.FC<PriceComponentAddButtonProps> = ({ onAdd, remainingKeys }) => {
  if (remainingKeys.length === 0) return null;
  const remaining = CONTRACT_PRICE_COMPONENTS.filter(component => remainingKeys.includes(component.key));
  const groups = groupsInCatalogOrder(remainingKeys);

  const options = groups.flatMap((group, groupIndex) => [
    { id: `group-${group}`, label: group, type: 'header' as const },
    ...remaining
      .filter(component => component.group === group)
      .map(component => ({ id: component.key, label: component.label })),
    ...(groupIndex < groups.length - 1
      ? [{ divider: 'bottom' as const, id: `divider-${group}`, type: 'divider' as const }]
      : []),
  ]);

  return (
    <MenuDropdown
      // `buttonVariant` has no `'outline-brand'` option (only 'base' | 'neutral' | 'brand' |
      // 'destructive' | 'icon') — the real `.slds-button_outline-brand` utility class layered on
      // via `buttonClassName` gets the exact look the Sulfur/BTU references use for this trigger
      // ("+ Add Component"/"+ Add Range") without a custom CSS rule.
      buttonClassName="slds-button_outline-brand"
      buttonVariant="neutral"
      iconCategory={IconCategory.Utility}
      iconName="add"
      iconPosition="left"
      label="Add Component"
      onSelect={option => {
        if (option.id) onAdd(option.id);
      }}
      options={options}
    />
  );
};

interface PriceComponentsTableProps {
  /** See SpecSectionPanel's own doc comment — only passed `true` for Range (see
   * ProductPriceBasisDetails). */
  collapsible?: boolean;
  /** Stretches this panel to fill its sibling column's height (see SpecSectionPanel's own doc
   * comment) instead of sitting at its natural, often much shorter, content height. */
  fillHeight?: boolean;
  onAdd: (key: string) => void;
  onRemove: (key: string) => void;
  selectedKeys: string[];
}

/**
 * Read-only "Price Components" breakdown — each row's dollar value comes from the shared
 * `PRICE_COMPONENTS` catalog (standing in for the live pricing formula engine, see that catalog's
 * own doc comment) and is never editable here; only *which* components apply is. `Total Basis`
 * sums every selected row's signed value (`resolvePriceComponentsTotal`) and is the number
 * `QualityAdjustmentManager` actually scales `percentFactor` against for this spec — see
 * `resolveEffectiveTradePrice`.
 */
export const PriceComponentsTable: React.FC<PriceComponentsTableProps> = ({
  collapsible,
  fillHeight,
  onAdd,
  onRemove,
  selectedKeys,
}) => {
  const [opened, setOpened] = useState(true);
  // Rows pending their exit animation — still rendered (and still counted in `selectedComponents`,
  // since `selectedKeys` hasn't changed yet) but faded via AnimatedItem's `visible` prop until
  // the transition finishes and `finalizeRemove` actually calls `onRemove`.
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(() => new Set());
  const selectedComponents = CONTRACT_PRICE_COMPONENTS.filter(component => selectedKeys.includes(component.key));
  const remainingKeys = CONTRACT_PRICE_COMPONENTS.filter(component => !selectedKeys.includes(component.key)).map(
    component => component.key,
  );
  const total = resolvePriceComponentsTotal(selectedKeys);

  // Reopens the panel on every add — a component picked while collapsed (Range mode, see
  // SpecSectionPanel's own doc comment) should be visible immediately, not left hidden behind a
  // closed chevron the user has to notice and toggle themselves.
  const handleAdd = (key: string) => {
    onAdd(key);
    setOpened(true);
  };

  const handleRemove = (key: string) => {
    setRemovingKeys(previous => new Set(previous).add(key));
  };

  const finalizeRemove = (key: string) => {
    onRemove(key);
    setRemovingKeys(previous => {
      const next = new Set(previous);
      next.delete(key);
      return next;
    });
  };

  return (
    <SpecSectionPanel
      action={<PriceComponentAddButton onAdd={handleAdd} remainingKeys={remainingKeys} />}
      collapsedBadge={pluralize(selectedComponents.length, 'component')}
      collapsible={collapsible}
      description="Components used to calculate the basis for price adjustment."
      fillHeight={fillHeight}
      iconName="table"
      onToggle={
        collapsible
          ? () => {
              setOpened(previous => !previous);
            }
          : undefined
      }
      opened={collapsible ? opened : undefined}
      title="Price Components"
      tooltip={FIELD_HELP.priceBasisPriceComponents}
    >
      {selectedComponents.length === 0 ? (
        // Matches the Sulfur reference's own footnote tone/length under a populated table ("Other
        // contract components can be added here. For example, Premiums (+$2.00) is available but
        // not selected.") rather than a single short line — this is the one state where this
        // panel would otherwise read as much shorter/more "unfinished" than its populated
        // sibling, right next to a Playground column that's already its own full height.
        <p className="slds-text-body_small slds-text-color_weak">
          No components selected yet. Other contract components can be added here — for example,{' '}
          {EXAMPLE_PLACEHOLDER_COMPONENT ? (
            <>
              {EXAMPLE_PLACEHOLDER_COMPONENT.label} ({EXAMPLE_PLACEHOLDER_COMPONENT.sign < 0 ? '−' : '+'}$
              {EXAMPLE_PLACEHOLDER_COMPONENT.value.toFixed(2)})
            </>
          ) : null}{' '}
          is available but not selected.
        </p>
      ) : (
        <div className="slds-scrollable_x">
          <table className="slds-table slds-table_cell-buffer" style={{ minWidth: 320 }}>
            <thead>
              <tr className="slds-line-height_reset">
                <th scope="col">
                  <div className="slds-truncate">Component</div>
                </th>
                <th className="slds-text-align_right" scope="col" style={{ width: 120 }}>
                  <div className="slds-truncate">Value ($)</div>
                </th>
                <th scope="col" style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {selectedComponents.map(component => (
                <AnimatedItem
                  key={component.key}
                  onExited={() => {
                    finalizeRemove(component.key);
                  }}
                  visible={!removingKeys.has(component.key)}
                >
                  {styles => (
                    <tr style={styles}>
                      <td>{component.label}</td>
                      {/* Plain, right-aligned text (not boxed like an input) — the value itself
                          comes from the contract's pricing formula, not entered here, matching
                          the Sulfur Price Components reference's own plain-text value column (see
                          this component's own doc comment on why it's read-only). */}
                      <td className="slds-text-align_right">
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{component.value.toFixed(2)}</span>
                      </td>
                      <td>
                        <Button
                          assistiveText={{ icon: `Remove ${component.label}` }}
                          iconCategory={IconCategory.Utility}
                          iconName="delete"
                          onClick={() => {
                            handleRemove(component.key);
                          }}
                          variant="icon"
                        />
                      </td>
                    </tr>
                  )}
                </AnimatedItem>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="slds-text-title_bold">Total Basis</td>
                <td className="slds-text-title_bold slds-text-align_right">${total.toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </SpecSectionPanel>
  );
};
